/**
 * THE FREE LISTENING TASTER (job #573, Aran 2026-11-05). PURE: no Vue, no
 * database, so the API's entitlement grid and the app's Listening overlay read
 * the very same rule. entitlementGrid.ts re-exports it and stamps it on the
 * refused listening_mode cell; the app imports it from here, because a signed-
 * out learner has no grid to read and must still get the taster.
 *
 * The rule: on a PREMIUM course, a learner without the £15 plan may open
 * Listening Mode and play the first `scenes` scenes of Pod `pod`, in the
 * Dialogues tab. Everything else in Listening Mode keeps the padlock.
 *
 * How the served data names them: Pod 1 is the pod whose slug is `pod-1`
 * (`listening_pods.id` = `<course>:pod-1`, servedPod.ts rule 1 — every
 * course's core pod, always first in the Listening list). Scene order is the
 * order the app already lists them in: `listening_pod_sentences.scene_number`,
 * bucketed and sorted ascending per pod (useListeningPods.buildPodScenes). The
 * taster counts POSITIONS in that list, so a pod whose numbering skips still
 * gives exactly five scenes.
 */
export const FREE_LISTENING_TASTER = { pod: 1, scenes: 5 } as const

export type ListeningTaster = { readonly pod: number; readonly scenes: number }

/** The slug of the taster's pod, as `listening_pods.id` carries it after the colon. */
export function tasterPodSlug(taster: ListeningTaster = FREE_LISTENING_TASTER): string {
  return `pod-${taster.pod}`
}

/** Is this pod (by its `listening_pods.id`, `<course>:<slug>`) the taster's pod? */
export function isTasterPod(podId: string, taster: ListeningTaster = FREE_LISTENING_TASTER): boolean {
  const slug = podId.slice(podId.lastIndexOf(':') + 1)
  return slug === tasterPodSlug(taster)
}

/** May the taster play the scene at this 0-based position of this pod's list? */
export function tasterAllowsScene(podId: string, position: number, taster: ListeningTaster = FREE_LISTENING_TASTER): boolean {
  return isTasterPod(podId, taster) && Number.isInteger(position) && position >= 0 && position < taster.scenes
}
